import { useState, useEffect } from 'react';
import { Grid, Typography, Box, CardContent, CardHeader, Card, Stack, Divider, Button, Tooltip, FormHelperText, FormControl, InputLabel, OutlinedInput, Select, MenuItem } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../../../../services/ApiService';
import { Agent, Business, Company } from '../../../types/UserTypes';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../../support/LoadingSpinner';
import Swal from 'sweetalert2'
import { BVTConstants } from '../../../../utils/BVTConstants';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveIcon from '@mui/icons-material/Save';
import { paths } from '../../../../utils/paths';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import { useDocuments } from '../../../../hooks/useDocuments';
import { Document } from '../../../types/DocumentType';
import { Iconify } from '../../../core/iconify';

export function BusinessBasicDetails(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = (location.state as { userId?: number })?.userId;
  const { user } = useAuthContext();
  const { saveDocument, getDocumentUrl } = useDocuments();
  const isAdmin = user?.role === 'ADMIN';

  const [businessData, setBusinessData] = useState<Business | null>(null);
  const [error, setError] = useState<string>('');
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditingAgent, setIsEditingAgent] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [logoData, setLogoData] = useState<Document>({
    id: 0,
    fileName: '',
    purpose: '',
    contentFile: null,
    contentBytes: null,
    status: '',
    mimeType: '',
    createdAt: '',
    updatedAt: '',
  });
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isEditingWebLink, setIsEditingWebLink] = useState(false);
  const [webLinkValue, setWebLinkValue] = useState('');

  useEffect(() => {
    fetchBusinessData();
  }, []);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setLoading(true);
        const response = await axios.post("/getCompanies", false);
        setCompanies(response.data);
      } catch (error) {
        console.error('Error fetching agents.');
      } finally {
        setLoading(false);
      }
    };
    fetchAgents();
  }, []);

  useEffect(() => {
    const fetchLogo = async () => {
      if (businessData?.logoId) {
        try {
          const url = await getDocumentUrl(businessData.logoId);
          setLogoUrl(url);
        } catch (error) {
          console.error('Error fetching logo:', error);
        }
      }
    };
    fetchLogo();
  }, [businessData?.logoId]);

  const fetchBusinessData = async () => {
    try {
      setDataLoading(true);
      let response;
      if (userId) {
        response = await axios.post("/getBusinessByUserId", userId);
      } else {
        response = await axios.get("/getBusiness");
      }
      setBusinessData(response.data);
    } catch (error) {
      setError('Failed to load User.');
      console.error('Error fetching business data.');
    } finally {
      setDataLoading(false);
    }
  };

  const handleLogoUpdate = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      
      if (!validTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, JPG, or PNG)');
        return;
      }

      if (file.size > 1024 * 1024) { // 1MB limit
        toast.error('File size should not exceed 1 MB');
        return;
      }

      setLoading(true);
      try {
        const documentData = {
          ...logoData,
          contentFile: file,
          fileName: file.name,
          purpose: BVTConstants.BUSINESS_LOGO
        };

        setLogoData(documentData);

        const docId = await saveDocument(documentData);
        
        if (businessData) {
          const updatedBusinessData = {
            ...businessData,
            logoId: docId
          };
          await axios.post("/updateBusinessLogo", updatedBusinessData);
          setBusinessData(updatedBusinessData);
          
          const newLogoUrl = await getDocumentUrl(docId);
          setLogoUrl(newLogoUrl);
          
          toast.success("Business logo updated successfully");
        }
      } catch (error) {
        console.error('Error updating logo');
        toast.error("Failed to update logo");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleWebLinkUpdate = async () => {
    if (!businessData) return;
    
    setLoading(true);
    try {
      const updatedBusinessData = {
        ...businessData,
        webLink: webLinkValue
      };
      await axios.post("/updateBusinessBaseProfile", updatedBusinessData);
      setBusinessData(updatedBusinessData);
      setIsEditingWebLink(false);
      toast.success("Web link updated successfully");
    } catch (error) {
      console.error('Error updating web link');
      toast.error("Failed to update web link");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignWorkflow = () => {
    if (user?.status === BVTConstants.SUSPENDED) {
      toast.error('Account is suspended. Please check pending dues.');
      return;
    }
    
    const path = isAdmin ? paths.admin.assignWorkflow : paths.agent.assignWorkflow;
    if (businessData) {
      navigate(path, { 
        state: { userId: businessData.userId } 
      });
    }
  };

  const updateStatusClick = async () => {
    const activeText: string = businessData?.status === BVTConstants.ACTIVE ? 'Deactivate' : 'Activate';
    Swal.fire({title: activeText + ' Business ?', 
      text: 'Do you want to '+ activeText +' the business user ' + businessData?.displayName + '?', 
      icon: 'question', 
      confirmButtonText: activeText, 
      cancelButtonText: 'Cancel', 
      showCancelButton: true }
    ).then((result) => {
      if(result['isConfirmed']){
        const req = {id: businessData.userId, status: businessData.status === BVTConstants.ACTIVE ? BVTConstants.INACTIVE : BVTConstants.ACTIVE}
        changeStatus(req);
      }
    })
  }

  const updateAgentClick = async () => {
    if(selectedAgent !== ''){
      Swal.fire({title: 'Update Agent ?', 
        text: 'Do you want to update the agent for ' + businessData?.displayName + '?', 
        icon: 'question', 
        confirmButtonText: 'Update', 
        cancelButtonText: 'Cancel', 
        showCancelButton: true }
      ).then((result) => {
        if(result['isConfirmed']){
          updateAgent();
        }
      })
    }
  }

  const updateAgent = async () => {
    try {
      setLoading(true);
      businessData.agentCompanyId = Number(selectedAgent);
      const response = await axios.post("/updateBusinessAgent", businessData);
      if(response.status === 200){
        toast.success('Business User Updated Successfully');
        fetchBusinessData();
      }
    } catch (error) {
      console.error('Error updating business user.');
    } finally {
      setLoading(false);
    }
  }

  const archiveBusiness = async () => {
    Swal.fire({title: 'Delete Business ?', 
      text: 'Do you want to delete the business user ' + businessData?.displayName + '?', 
      icon: 'question', 
      confirmButtonText: 'Delete', 
      cancelButtonText: 'Cancel', 
      showCancelButton: true }
    ).then((result) => {
      if(result['isConfirmed']){
        const req = {id: businessData.userId, status: BVTConstants.ARCHIVED}
        changeStatus(req);
      }
    })
  }

  const changeStatus = async (req: any) => {
    try {
      setLoading(true);
      const response = await axios.post("/updateUserStatus", req);
      if(response.status === 200){
        toast.success('Business User Updated Successfully');
        if(req.status === BVTConstants.ARCHIVED){
          navigate(-1);
        }else{
          fetchBusinessData();
        }
      }
    } catch (error) {
      console.error('Error updating business user.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
    {dataLoading ? (
      <LoadingSpinner loading={dataLoading} />
    ) : error ? (
      <Typography variant="h6" color="error" textAlign="center">
        {error}
      </Typography>
    ) : (
      <Stack spacing={2}>
        <Card>
          <CardHeader 
            title={businessData?.displayName}
            subheader={businessData?.status}
            action={
              <>
                {(isAdmin || user?.role === BVTConstants.USER_TYPE_AGENT || user?.role === BVTConstants.USER_TYPE_COMPANY) && (
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{marginRight: 1}}
                    onClick={handleAssignWorkflow}
                  >
                    Assign Workflow
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    variant="outlined"
                    sx={{marginRight: 1}}
                    onClick={() => navigate(paths.admin.editBusinessBase, { state: { userId: businessData?.userId } })}
                  >
                    Edit
                  </Button>
                )}
                {isAdmin && (
                  <Button
                    variant="outlined"
                    sx={{marginRight: 1}}
                    onClick={() => updateStatusClick()}
                  >
                    {businessData?.status === BVTConstants.ACTIVE ? 'Deactivate' : 'Activate'}
                  </Button>
                )}
                {isAdmin && (
                  <Tooltip title="Archive Business User" arrow>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => archiveBusiness()}
                    >
                      <DeleteOutlineIcon/>
                    </Button>
                  </Tooltip>
                )}
                <IconButton 
                  aria-label="close" 
                  onClick={() => navigate(-1)}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    ml: 1
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </>
            }
          />
          <Divider />
          <CardContent>
            <Box sx={{ maxWidth: '900px' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={10}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Legal Name
                      </Typography>
                      <Typography variant="body1">{businessData?.legalName}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Trade Name
                      </Typography>
                      <Typography variant="body1">{businessData?.tradeName}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        ABN
                      </Typography>
                      <Typography variant="body1">{businessData?.businessABN}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Address
                      </Typography>
                      <Typography variant="body1">{businessData?.address}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Contact Person First Name
                      </Typography>
                      <Typography variant="body1">{businessData?.firstName}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Contact Person Last Name
                      </Typography>
                      <Typography variant="body1">{businessData?.lastName}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Email
                      </Typography>
                      <Typography variant="body1">{businessData?.email}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Contact
                      </Typography>
                      <Typography variant="body1">{businessData?.contact}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Web Link
                        </Typography>
                        {!isEditingWebLink && (
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              setIsEditingWebLink(true);
                              setWebLinkValue(businessData?.webLink || '');
                            }}
                            sx={{ color: 'warning.main' }}
                          >
                            <Iconify icon="eva:edit-2-fill" />
                          </IconButton>
                        )}
                      </Box>
                      {isEditingWebLink ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FormControl fullWidth>
                            <OutlinedInput
                              size="small"
                              value={webLinkValue}
                              onChange={(e) => setWebLinkValue(e.target.value)}
                              placeholder="Enter web link"
                              inputProps={{ maxLength: 500 }}
                            />
                          </FormControl>
                          <IconButton 
                            size="small" 
                            onClick={handleWebLinkUpdate}
                            sx={{ color: 'success.main' }}
                          >
                            <Iconify icon="eva:checkmark-circle-2-fill" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              setIsEditingWebLink(false);
                              setWebLinkValue(businessData?.webLink || '');
                            }}
                            sx={{ color: 'error.main' }}
                          >
                            <Iconify icon="eva:close-circle-fill" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Tooltip title={businessData?.webLink || 'N/A'}>
                          <Typography variant="body1">
                            {businessData?.webLink ? (
                              <a 
                                href={businessData.webLink.startsWith('http') ? businessData.webLink : `https://${businessData.webLink}`}
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ color: 'inherit', textDecoration: 'none' }}
                              >
                                {businessData.webLink.length > 35 
                                  ? `${businessData.webLink.substring(0, 35)}...` 
                                  : businessData.webLink}
                              </a>
                            ) : (
                              'N/A'
                            )}
                          </Typography>
                        </Tooltip>
                      )}
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Agent
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isEditingAgent && isAdmin ? (
                          <>
                            <Select
                              size="small"
                              value={selectedAgent}
                              onChange={(e) => setSelectedAgent(e.target.value)}
                              sx={{ flexGrow: 1 }}
                            >
                              {companies.map((company) => (
                                <MenuItem key={company.companyId} value={company.companyId}>
                                  {company.companyName}
                                </MenuItem>
                              ))}
                            </Select>
                            <IconButton 
                              onClick={() => {
                                updateAgentClick();
                                setIsEditingAgent(false);
                              }}
                              size="small"
                              color="success"
                              sx={{ 
                                border: '1px solid',
                                borderColor: 'divider',
                                ml: 1
                              }}
                            >
                              <SaveIcon />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <Typography variant="body1">
                              {businessData?.agentCompanyName}
                            </Typography>
                            {isAdmin && (
                              <IconButton 
                                onClick={() => {
                                  setIsEditingAgent(true);
                                }}
                                size="small"
                                color="success"
                                sx={{ 
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  ml: 2
                                }}
                              >
                                <EditOutlinedIcon />
                              </IconButton>
                            )}
                          </>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Created At
                      </Typography>
                      <Typography variant="body1">{businessData?.createdAt}</Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Last Login
                      </Typography>
                      <Typography variant="body1">{businessData?.lastLoginAt}</Typography>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Box 
                    sx={{ 
                      width: '100%', 
                      height: 160, 
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    {logoUrl ? (
                      <img 
                        src={logoUrl}
                        alt="Business Logo"
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%', 
                          objectFit: 'contain' 
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No Logo Available
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                  >
                    Update Logo
                    <input
                      type="file"
                      hidden
                      accept=".jpg,.jpeg,.png"
                      onChange={handleLogoUpdate}
                    />
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
        <LoadingSpinner loading={loading} />
      </Stack>
    )}
    </>
  );
}
